// Email Action Buttons - Hover-only UI with full AI access
// Buttons appear only on hover for clean UI, but AI can access all features anytime

import React from 'react';
import { Button } from './ui/button';
import {
  Reply, ReplyAll, Forward, Archive, Trash2, Star, Mail, MailOpen,
  Clock, Pin, MoreVertical, Tag, FolderInput, Ban, AlertTriangle
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';
import { emailActions } from '@/services/emailActions';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

interface EmailActionButtonsProps {
  emailId: string;
  isStarred?: boolean;
  isRead?: boolean;
  isPinned?: boolean;
  variant?: 'compact' | 'full';
  onAction?: (action: string) => void;
}

export function EmailActionButtons({
  emailId, isStarred = false, isRead = false, isPinned = false,
  variant = 'compact', onAction
}: EmailActionButtonsProps) {
  const handleAction = async (action: string, callback: () => Promise<void>) => {
    try {
      await callback();
      onAction?.(action);
    } catch (error) {
      console.error(`Failed to execute action: ${action}`, error);
    }
  };

  if (variant === 'compact') {
    return (
      <TooltipProvider delayDuration={100}>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); onAction?.('reply'); }}>
                <Reply className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Reply</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); handleAction('star', () => emailActions.star(emailId, !isStarred)); }}>
                <Star className={`h-3.5 w-3.5 ${isStarred ? 'fill-yellow-500 text-yellow-500' : ''}`} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isStarred ? 'Unstar' : 'Star'}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); handleAction('archive', () => emailActions.archive(emailId)); }}>
                <Archive className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Archive</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7"
                onClick={(e) => { e.stopPropagation(); handleAction('trash', () => emailActions.trash(emailId)); }}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete</TooltipContent>
          </Tooltip>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleAction('reply-all', () => emailActions.reply(emailId, '', true)); }}>
                <ReplyAll className="h-4 w-4 mr-2" />Reply All
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onAction?.('forward'); }}>
                <Forward className="h-4 w-4 mr-2" />Forward
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleAction('mark-read', () => emailActions.markRead(emailId, !isRead)); }}>
                {isRead ? <Mail className="h-4 w-4 mr-2" /> : <MailOpen className="h-4 w-4 mr-2" />}
                Mark as {isRead ? 'Unread' : 'Read'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleAction('important', () => emailActions.markImportant(emailId, true)); }}>
                <AlertTriangle className="h-4 w-4 mr-2" />Mark Important
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleAction('pin', () => emailActions.pin(emailId, !isPinned)); }}>
                <Pin className="h-4 w-4 mr-2" />{isPinned ? 'Unpin' : 'Pin'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onAction?.('snooze'); }}>
                <Clock className="h-4 w-4 mr-2" />Snooze
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onAction?.('add-label'); }}>
                <Tag className="h-4 w-4 mr-2" />Add Label
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onAction?.('move'); }}>
                <FolderInput className="h-4 w-4 mr-2" />Move to Folder
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleAction('spam', () => emailActions.markSpam(emailId)); }}>
                <Ban className="h-4 w-4 mr-2" />Mark as Spam
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleAction('block', () => emailActions.blockSender(emailId)); }}>
                <Ban className="h-4 w-4 mr-2" />Block Sender
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      <Button variant="outline" size="sm" onClick={() => handleAction('reply', async () => onAction?.('reply'))}>
        <Reply className="h-3.5 w-3.5 mr-1.5" />Reply
      </Button>
      <Button variant="outline" size="sm" onClick={() => handleAction('reply-all', () => emailActions.reply(emailId, '', true))}>
        <ReplyAll className="h-3.5 w-3.5 mr-1.5" />Reply All
      </Button>
      <Button variant="outline" size="sm" onClick={() => handleAction('forward', async () => onAction?.('forward'))}>
        <Forward className="h-3.5 w-3.5 mr-1.5" />Forward
      </Button>
      <Button variant="outline" size="sm" onClick={() => handleAction('archive', () => emailActions.archive(emailId))}>
        <Archive className="h-3.5 w-3.5 mr-1.5" />Archive
      </Button>
      <Button variant="outline" size="sm" onClick={() => handleAction('trash', () => emailActions.trash(emailId))}>
        <Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete
      </Button>
    </div>
  );
}
